import React, { useEffect, useState } from "react";
import { Bot, Coins, MessageSquare, Calculator, ShoppingCart, Sparkles, LogIn, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import TauroChat from "../components/TauroChat";
import TauroAnalyzer from "../components/TauroAnalyzer";
import CreditsModal from "../components/CreditsModal";
import AuthModal from "../components/AuthModal";
import { useAuth } from "../context/AuthContext";
import { stripeStatus } from "../paymentsApi";
import { useToast } from "../hooks/use-toast";

const TABS = [
  { id: "chat", label: "Chat con Tauro", icon: MessageSquare },
  { id: "analyze", label: "Generador", icon: Calculator },
];

export default function Tauro() {
  const { user, isAuthenticated, loading, refreshUser } = useAuth();
  const [tab, setTab] = useState("chat");
  const [creditsOpen, setCreditsOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [paymentBanner, setPaymentBanner] = useState(null); // { kind, message }
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Handle Stripe return: ?session_id=... or ?cancelled=1
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get("session_id");
    const cancelled = params.get("cancelled");
    if (cancelled) {
      setPaymentBanner({ kind: "cancel", message: "Pago cancelado. Puedes intentarlo de nuevo cuando quieras." });
      navigate("/tauro", { replace: true });
      return;
    }
    if (!sid) return;
    setPaymentBanner({ kind: "loading", message: "Verificando tu pago..." });
    let attempts = 0;
    let stop = false;

    const poll = async () => {
      if (stop) return;
      attempts += 1;
      try {
        const tx = await stripeStatus(sid);
        if (tx.payment_status === "paid" || tx.status === "credited") {
          setPaymentBanner({ kind: "success", message: `¡Pago exitoso! Se acreditaron ${tx.credits} créditos a tu cuenta.` });
          await refreshUser();
          toast({ title: "Pago exitoso", description: `+${tx.credits} créditos añadidos.` });
          navigate("/tauro", { replace: true });
          return;
        }
        if (tx.status === "expired" || tx.payment_status === "failed") {
          setPaymentBanner({ kind: "fail", message: "El pago no se completó. Intenta de nuevo." });
          navigate("/tauro", { replace: true });
          return;
        }
        if (attempts < 10) setTimeout(poll, 2000);
        else setPaymentBanner({ kind: "fail", message: "No pudimos confirmar el pago. Si descontaron tu tarjeta, contáctanos." });
      } catch (e) {
        if (attempts < 10) setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { stop = true; };
  }, [location.search, navigate, refreshUser, toast]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50/30 to-white">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0e1b4a] via-[#0e4a7a] to-[#1d6cb1] text-white">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #f59e0b 0%, transparent 40%), radial-gradient(circle at 80% 80%, #ec4899 0%, transparent 40%)" }} />
        <div className="relative max-w-5xl mx-auto px-6 py-14 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-200 text-xs font-bold mb-4">
            <Sparkles className="w-3.5 h-3.5" /> TAURO
          </div>
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 via-orange-500 to-red-500 flex items-center justify-center shadow-xl">
              <Bot className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-extrabold text-4xl sm:text-5xl" style={{ fontFamily: "'Playfair Display', serif" }}>
              TAURO
            </h1>
          </div>
          <p className="text-lg sm:text-xl text-blue-100 font-semibold">Super analista matemático de lotería dominicana</p>
          <p className="mt-3 text-sm text-blue-100/90 max-w-2xl mx-auto">
            Tauro combina estadística avanzada, numerología aplicada, análisis de patrones, interpretación de sueños y la tabla jaladora para generar recomendaciones personalizadas para ti.
          </p>
        </div>
      </section>

      {/* Panel */}
      <section className="max-w-6xl mx-auto px-4 py-10">
        {loading ? (
          <p className="text-center text-gray-500">Cargando...</p>
        ) : !isAuthenticated ? (
          <div className="max-w-lg mx-auto bg-white border border-blue-100 rounded-2xl p-8 text-center shadow-md">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-amber-500 to-red-600 flex items-center justify-center mb-4 shadow">
              <LogIn className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl font-extrabold text-[#0e4a7a] mb-2">Panel solo para registrados</h2>
            <p className="text-gray-600 text-sm mb-5">Crea tu cuenta gratis y recibe <strong>3 créditos de bienvenida</strong> para probar a Tauro.</p>
            <button
              onClick={() => setAuthOpen(true)}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-[#1d6cb1] to-[#2a83ce] text-white font-bold shadow hover:shadow-lg transition"
            >
              Iniciar sesión / Registrarme
            </button>
            <p className="mt-4 text-xs text-gray-500">
              <Link to="/" className="text-[#1d6cb1] hover:underline">← Volver al inicio</Link>
            </p>
          </div>
        ) : (
          <>
            {/* Payment status banner */}
            {paymentBanner && (
              <div className={`mb-4 px-4 py-3 rounded-2xl flex items-center gap-3 border ${
                paymentBanner.kind === "success" ? "bg-emerald-50 border-emerald-200 text-emerald-800" :
                paymentBanner.kind === "fail" ? "bg-red-50 border-red-200 text-red-800" :
                paymentBanner.kind === "cancel" ? "bg-amber-50 border-amber-200 text-amber-800" :
                "bg-blue-50 border-blue-200 text-blue-800"
              }`} data-testid="payment-banner">
                {paymentBanner.kind === "success" && <CheckCircle2 className="w-5 h-5" />}
                {paymentBanner.kind === "fail" && <XCircle className="w-5 h-5" />}
                {paymentBanner.kind === "cancel" && <XCircle className="w-5 h-5" />}
                {paymentBanner.kind === "loading" && <Loader2 className="w-5 h-5 animate-spin" />}
                <p className="text-sm font-semibold flex-1">{paymentBanner.message}</p>
                <button onClick={() => setPaymentBanner(null)} className="text-xs opacity-70 hover:opacity-100">Cerrar</button>
              </div>
            )}

            {/* Credits bar */}
            <div className="bg-white rounded-2xl shadow-md border border-blue-100 p-5 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow">
                  <Coins className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500">Créditos Tauro</p>
                  <p className="text-3xl font-extrabold text-[#0e4a7a]" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {user?.credits ?? 0}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-600">
                <div className="hidden sm:block text-right">
                  <p><span className="font-bold">1 chat</span> = 1 crédito</p>
                  <p><span className="font-bold">1 análisis</span> = 2 créditos</p>
                </div>
                <button
                  onClick={() => setCreditsOpen(true)}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white font-bold shadow hover:shadow-lg hover:scale-[1.02] transition"
                >
                  <ShoppingCart className="w-4 h-4" /> Comprar créditos
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex bg-blue-50 rounded-full p-1 mb-6 max-w-md mx-auto">
              {TABS.map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition ${
                      tab === t.id ? "bg-white text-[#0e4a7a] shadow" : "text-gray-600"
                    }`}
                  >
                    <Icon className="w-4 h-4" /> {t.label}
                  </button>
                );
              })}
            </div>

            {tab === "chat" && (
              <TauroChat onBuyCredits={() => setCreditsOpen(true)} />
            )}
            {tab === "analyze" && (
              <TauroAnalyzer onBuyCredits={() => setCreditsOpen(true)} />
            )}

            <div className="mt-6 text-center">
              <Link to="/" className="text-sm text-[#1d6cb1] hover:underline">← Volver al inicio</Link>
            </div>
          </>
        )}
      </section>

      <Footer />

      <CreditsModal
        open={creditsOpen}
        onOpenChange={setCreditsOpen}
        onPurchased={() => { /* AuthContext user will refresh */ }}
      />
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} defaultTab="register" />
    </div>
  );
}
